import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Field, inputClass, selectClass } from './BackgroundFields';
import { WEEKDAY_KEYS, WEEKDAY_LABELS, getDayRanges, normalizeDaySlot } from '../utils/commerceAdmin';

const MAX_RANGES = 5;

function ensureSlot(slot) {
  return normalizeDaySlot(slot, { open: false, from: '12:00', to: '22:00' });
}

export default function WeekScheduleEditor({ value, onChange, title, subtitle }) {
  const patchDay = (key, nextSlot) => {
    onChange({ ...value, [key]: ensureSlot(nextSlot) });
  };

  return (
    <div className="rounded-xl border border-border overflow-hidden bg-card shadow-sm">
      {title ? (
        <div className="px-4 py-3 border-b border-border bg-muted/20">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {subtitle ? (
            <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
          ) : null}
        </div>
      ) : null}
      <div className="divide-y divide-border">
        {WEEKDAY_KEYS.map((key) => {
          const slot = ensureSlot(value?.[key]);
          const isOpen = slot.open === true;
          const ranges = getDayRanges(slot);

          return (
            <div key={key} className="px-4 py-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-foreground min-w-[100px]">
                  {WEEKDAY_LABELS[key]}
                </span>
                <div className="flex-1 min-w-[160px] max-w-xs">
                  <select
                    className={selectClass}
                    value={isOpen ? 'open' : 'closed'}
                    aria-label={`Disponibilité ${WEEKDAY_LABELS[key]}`}
                    onChange={(e) => {
                      const open = e.target.value === 'open';
                      const nextRanges =
                        open && !ranges.length
                          ? [{ from: '12:00', to: '14:00' }]
                          : ranges;
                      patchDay(key, {
                        ...slot,
                        open,
                        ranges: nextRanges,
                        from: nextRanges[0]?.from ?? slot.from,
                        to: nextRanges[0]?.to ?? slot.to,
                      });
                    }}
                  >
                    <option value="open">Ouvert</option>
                    <option value="closed">Fermé</option>
                  </select>
                </div>
              </div>

              {isOpen ? (
                <div className="space-y-2 pl-0 sm:pl-[100px]">
                  {ranges.map((range, idx) => (
                    <div
                      key={`${key}-range-${idx}`}
                      className="flex flex-wrap items-end gap-2 rounded-lg border border-border/80 bg-muted/10 p-3"
                    >
                      <Field label={idx === 0 ? 'Créneau' : `Créneau ${idx + 1}`}>
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            className={inputClass}
                            value={range.from}
                            onChange={(e) => {
                              const next = ranges.map((r, i) =>
                                i === idx ? { ...r, from: e.target.value } : r,
                              );
                              patchDay(key, { ...slot, ranges: next, from: next[0]?.from });
                            }}
                          />
                          <span className="text-xs text-muted-foreground shrink-0">à</span>
                          <input
                            type="time"
                            className={inputClass}
                            value={range.to}
                            onChange={(e) => {
                              const next = ranges.map((r, i) =>
                                i === idx ? { ...r, to: e.target.value } : r,
                              );
                              patchDay(key, { ...slot, ranges: next, to: next[0]?.to });
                            }}
                          />
                        </div>
                      </Field>
                      {ranges.length > 1 ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-destructive shrink-0 mb-0.5"
                          onClick={() => {
                            const next = ranges.filter((_, i) => i !== idx);
                            patchDay(key, {
                              ...slot,
                              ranges: next.length ? next : [{ from: '12:00', to: '14:00' }],
                            });
                          }}
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      ) : null}
                    </div>
                  ))}
                  {ranges.length < MAX_RANGES ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-1"
                      onClick={() => {
                        patchDay(key, {
                          ...slot,
                          ranges: [
                            ...ranges,
                            { from: '18:00', to: '22:00' },
                          ],
                        });
                      }}
                    >
                      <Icon name="Plus" size={14} className="mr-1" />
                      Ajouter une coupure (ex. service du soir)
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
